import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Dialog,
  DialogContent,
} from '@duck/ui'
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline'
import { LocalizedLink as Link } from '@/components/LocalizedLink'
import { useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { WaitingList } from '@components/WaitingList/WaitingList'
import { useTar } from '@/modules'
import { trackFaq } from '@/modules/faqAnalytics'
import { trackMarketing } from '@/modules/marketingAnalytics'
import { StructuredDataScript } from '@/modules/seo'
import type { TranslationKey } from '@/modules/i18n'
import { ExternalURLs } from '@/types/externalURLs'

const SITE_URL = 'https://www.xp-lab.com'

const sections = [
  {
    id: 'getting-started',
    titleKey: 'faq.sectionProductBasicsTitle' as TranslationKey,
    descriptionKey: 'faq.sectionProductBasicsDescription' as TranslationKey,
    items: [
      'what-is-xp-lab',
      'how-is-xp-lab-different-from-a-sql-course',
      'who-is-xp-lab-for',
      'what-do-i-get',
      'is-xp-lab-free',
      'what-skills-should-i-already-have',
      'do-i-need-to-install-anything-before-i-start',
    ],
  },
  {
    id: 'practice-and-feedback',
    titleKey: 'faq.sectionLearningWorkflowTitle' as TranslationKey,
    descriptionKey: 'faq.sectionLearningWorkflowDescription' as TranslationKey,
    items: [
      'what-kind-of-tasks-will-i-solve',
      'how-long-does-an-assignment-usually-take',
      'do-i-get-feedback',
      'how-does-feedback-work-in-practice',
      'can-i-retry-a-challenge-after-i-submit',
      'can-i-use-llms-chatgpt-gemini-claude',
    ],
  },
  {
    id: 'career-growth',
    titleKey: 'faq.sectionCareerOutcomesTitle' as TranslationKey,
    descriptionKey: 'faq.sectionCareerOutcomesDescription' as TranslationKey,
    items: [
      'will-this-help-me-get-a-job',
      'how-does-xp-lab-help-with-take-home-assignments',
      'i-need-help-with-interviews-can-you-help-me',
      'what-is-anonymous-opportunity-matching',
    ],
  },
  {
    id: 'privacy-bugs-and-access',
    titleKey: 'faq.sectionTrustOperationsTitle' as TranslationKey,
    descriptionKey: 'faq.sectionTrustOperationsDescription' as TranslationKey,
    items: [
      'is-my-work-private',
      'what-happens-during-the-private-beta',
      'when-is-it-launching',
      'i-found-a-bug',
    ],
  },
]

const allFaqSlugs = new Set(sections.flatMap((s) => s.items))

const faqStructuredDataEntries: {
  questionKey: TranslationKey
  answerKeys: TranslationKey[]
  anchorSlug: string
  answerTextSuffix?: string
}[] = [
  {
    questionKey: 'faq.whatIsXpLab',
    answerKeys: ['faq.whatIsXpLabP1Suffix', 'faq.whatIsXpLabP2'],
    anchorSlug: 'what-is-xp-lab',
  },
  {
    questionKey: 'faq.howIsXpLabDifferentFromSqlCourse',
    answerKeys: [
      'faq.howIsXpLabDifferentFromSqlCourseP1',
      'faq.howIsXpLabDifferentFromSqlCourseP2',
    ],
    anchorSlug: 'how-is-xp-lab-different-from-a-sql-course',
  },
  {
    questionKey: 'faq.whoIsXpLabFor',
    answerKeys: [
      'faq.whoIsXpLabForIntro',
      'faq.whoIsXpLabForItem1',
      'faq.whoIsXpLabForItem2',
      'faq.whoIsXpLabForItem3',
      'faq.whoIsXpLabForItem4',
      'faq.whoIsXpLabForItem5',
      'faq.whoIsXpLabForOutro',
    ],
    anchorSlug: 'who-is-xp-lab-for',
  },
  {
    questionKey: 'faq.whatDoIGet',
    answerKeys: [
      'faq.whatDoIGetIntro',
      'faq.whatDoIGetItem1',
      'faq.whatDoIGetItem2',
      'faq.whatDoIGetItem3',
      'faq.whatDoIGetItem4',
    ],
    anchorSlug: 'what-do-i-get',
  },
  {
    questionKey: 'faq.isXpLabFree',
    answerKeys: ['faq.isXpLabFreeP1', 'faq.isXpLabFreeP2', 'faq.isXpLabFreeP3'],
    anchorSlug: 'is-xp-lab-free',
  },
  {
    questionKey: 'faq.whatSkills',
    answerKeys: [
      'faq.whatSkillsIntro',
      'faq.whatSkillsItem1',
      'faq.whatSkillsItem2',
      'faq.whatSkillsOutroPrefix',
      'faq.whatSkillsOutroBold',
    ],
    anchorSlug: 'what-skills-should-i-already-have',
  },
  {
    questionKey: 'faq.doINeedToInstallAnything',
    answerKeys: ['faq.doINeedToInstallAnythingP1', 'faq.doINeedToInstallAnythingP2'],
    anchorSlug: 'do-i-need-to-install-anything-before-i-start',
  },
  {
    questionKey: 'faq.whatKindOfTasks',
    answerKeys: [
      'faq.whatKindOfTasksIntro',
      'faq.whatKindOfTasksExamples',
      'faq.whatKindOfTasksItem1',
      'faq.whatKindOfTasksItem2',
      'faq.whatKindOfTasksItem3',
      'faq.whatKindOfTasksItem4',
      'faq.whatKindOfTasksOutroPrefix',
      'faq.whatKindOfTasksOutroBold',
    ],
    anchorSlug: 'what-kind-of-tasks-will-i-solve',
  },
  {
    questionKey: 'faq.howLongDoesAssignmentTake',
    answerKeys: [
      'faq.howLongDoesAssignmentTakeP1',
      'faq.howLongDoesAssignmentTakeItem1',
      'faq.howLongDoesAssignmentTakeItem2',
      'faq.howLongDoesAssignmentTakeItem3',
      'faq.howLongDoesAssignmentTakeP2',
    ],
    anchorSlug: 'how-long-does-an-assignment-usually-take',
  },
  {
    questionKey: 'faq.doIGetFeedback',
    answerKeys: [
      'faq.doIGetFeedbackIntro',
      'faq.doIGetFeedbackP2',
      'faq.doIGetFeedbackItem1',
      'faq.doIGetFeedbackItem2',
      'faq.doIGetFeedbackItem3',
      'faq.doIGetFeedbackOutro',
    ],
    anchorSlug: 'do-i-get-feedback',
  },
  {
    questionKey: 'faq.howDoesFeedbackWorkInPractice',
    answerKeys: [
      'faq.howDoesFeedbackWorkInPracticeP1',
      'faq.howDoesFeedbackWorkInPracticeItem1',
      'faq.howDoesFeedbackWorkInPracticeItem2',
      'faq.howDoesFeedbackWorkInPracticeItem3',
      'faq.howDoesFeedbackWorkInPracticeP2',
    ],
    anchorSlug: 'how-does-feedback-work-in-practice',
  },
  {
    questionKey: 'faq.canIRetryChallengeAfterSubmit',
    answerKeys: [
      'faq.canIRetryChallengeAfterSubmitP1',
      'faq.canIRetryChallengeAfterSubmitP2',
    ],
    anchorSlug: 'can-i-retry-a-challenge-after-i-submit',
  },
  {
    questionKey: 'faq.canIChatGPT',
    answerKeys: [
      'faq.canIChatGPTP1',
      'faq.canIChatGPTP2',
      'faq.canIChatGPTP3',
      'faq.canIChatGPTP4',
      'faq.canIChatGPTItem1',
      'faq.canIChatGPTItem2',
      'faq.canIChatGPTItem3',
      'faq.canIChatGPTItem4',
      'faq.canIChatGPTP5',
    ],
    anchorSlug: 'can-i-use-llms-chatgpt-gemini-claude',
  },
  {
    questionKey: 'faq.willThisHelpJob',
    answerKeys: [
      'faq.willThisHelpJobIntroPrefix',
      'faq.willThisHelpJobIntroBold',
      'faq.willThisHelpJobItem1',
      'faq.willThisHelpJobItem2',
      'faq.willThisHelpJobItem3',
      'faq.willThisHelpJobItem4',
      'faq.willThisHelpJobOutroPrefix',
      'faq.willThisHelpJobOutroBold',
      'faq.willThisHelpJobOutroSuffix',
    ],
    anchorSlug: 'will-this-help-me-get-a-job',
  },
  {
    questionKey: 'faq.howDoesXpLabHelpTakeHomes',
    answerKeys: [
      'faq.howDoesXpLabHelpTakeHomesP1',
      'faq.howDoesXpLabHelpTakeHomesP2',
      'faq.howDoesXpLabHelpTakeHomesP3',
    ],
    anchorSlug: 'how-does-xp-lab-help-with-take-home-assignments',
  },
  {
    questionKey: 'faq.mockInterviewFromCompletedTask',
    answerKeys: [
      'faq.mockInterviewFromCompletedTaskP1',
      'faq.mockInterviewFromCompletedTaskP2',
      'faq.mockInterviewFromCompletedTaskP3Prefix',
    ],
    answerTextSuffix: 'tal@xp-lab.com.',
    anchorSlug: 'i-need-help-with-interviews-can-you-help-me',
  },
  {
    questionKey: 'faq.anonymousOpportunityMatching',
    answerKeys: ['faq.anonymousOpportunityMatchingP1'],
    anchorSlug: 'what-is-anonymous-opportunity-matching',
  },
  {
    questionKey: 'faq.isMyWorkPrivate',
    answerKeys: ['faq.isMyWorkPrivateAnswerPrefix', 'common.privacyPolicy'],
    anchorSlug: 'is-my-work-private',
  },
  {
    questionKey: 'faq.whatHappensDuringPrivateBeta',
    answerKeys: [
      'faq.whatHappensDuringPrivateBetaP1',
      'faq.whatHappensDuringPrivateBetaItem1',
      'faq.whatHappensDuringPrivateBetaItem2',
      'faq.whatHappensDuringPrivateBetaItem3',
    ],
    anchorSlug: 'what-happens-during-the-private-beta',
  },
  {
    questionKey: 'faq.whenIsLaunching',
    answerKeys: [
      'faq.whenIsLaunchingPrefix',
      'faq.waitlist',
      'faq.whenIsLaunchingOrContact',
    ],
    answerTextSuffix: 'tal@xp-lab.com.',
    anchorSlug: 'when-is-it-launching',
  },
  {
    questionKey: 'faq.foundABug',
    answerKeys: ['faq.foundABugAnswerPrefix', 'faq.reportABug'],
    anchorSlug: 'i-found-a-bug',
  },
]

function buildFaqJsonLd(t: (key: TranslationKey) => string, locale: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: locale,
    mainEntity: faqStructuredDataEntries.map((entry) => ({
      '@type': 'Question',
      name: t(entry.questionKey),
      url: new URL(`/faq#${entry.anchorSlug}`, SITE_URL).toString(),
      acceptedAnswer: {
        '@type': 'Answer',
        text: [entry.answerKeys.map((k) => t(k)).join(' '), entry.answerTextSuffix]
          .filter(Boolean)
          .join(' '),
      },
    })),
  }
}

function FaqBugReportLink() {
  const { t } = useTar()
  return (
    <div className="">
      <a
        href={ExternalURLs.DiscordInviteIntoBugReportChannel}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackFaq('faq:bug_report:click', { slug: 'i-found-a-bug' })}
        className="text-sm font-medium text-accent hover:text-foreground transition-colors underline underline-offset-4"
      >
        {t('faq.reportABug')}
      </a>
    </div>
  )
}

function NextStep({
  labelKey,
  href,
  external,
  slug,
}: {
  labelKey: TranslationKey
  href: string
  external?: boolean
  slug: string
}) {
  const { t } = useTar()
  const linkClassName =
    'text-sm font-medium text-accent hover:text-foreground transition-colors underline underline-offset-4'
  const handleClick = () =>
    trackFaq('faq:next_step:click', {
      slug,
      target: href,
      external: Boolean(external),
    })

  return (
    <div className="">
      {external ? (
        <a
          href={href}
          onClick={handleClick}
          className={linkClassName}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t(labelKey)}
        </a>
      ) : (
        <Link to={href} onClick={handleClick} className={linkClassName}>
          {t(labelKey)}
        </Link>
      )}
    </div>
  )
}

function SectionExpandControls({
  sectionId,
  sectionItems,
  openItems,
  setOpenItems,
}: {
  sectionId: string
  sectionItems: string[]
  openItems: string[]
  setOpenItems: Dispatch<SetStateAction<string[]>>
}) {
  const { t } = useTar()
  const openInSection = sectionItems.filter((v) => openItems.includes(v)).length
  const allOpen = openInSection === sectionItems.length
  const allClosed = openInSection === 0

  const buttonClassName =
    'inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-accent/40 hover:bg-accent/5 hover:text-accent disabled:pointer-events-none disabled:opacity-50'

  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button
        type="button"
        disabled={allOpen}
        onClick={() => {
          trackFaq('faq:section:expand_all', { sectionId })
          setOpenItems((prev) => Array.from(new Set([...prev, ...sectionItems])))
        }}
        className={buttonClassName}
      >
        <ArrowsPointingOutIcon className="h-4 w-4" />
        {t('faq.expandAll')}
      </button>
      <button
        type="button"
        disabled={allClosed}
        onClick={() => {
          trackFaq('faq:section:collapse_all', { sectionId })
          const sectionSet = new Set(sectionItems)
          setOpenItems((prev) => prev.filter((v) => !sectionSet.has(v)))
        }}
        className={buttonClassName}
      >
        <ArrowsPointingInIcon className="h-4 w-4" />
        {t('faq.collapseAll')}
      </button>
    </div>
  )
}

function slugToSectionId(slug: string): string | undefined {
  return sections.find((s) => s.items.includes(slug))?.id
}

export const FaqPageStructured = () => {
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false)
  const [openItems, setOpenItems] = useState<string[]>(['what-is-xp-lab'])
  const openItemsRef = useRef(openItems)
  const didTrackInitialRef = useRef(false)
  const { t, locale } = useTar()
  const faqJsonLd = buildFaqJsonLd(t, locale)

  openItemsRef.current = openItems

  useEffect(() => {
    if (didTrackInitialRef.current) return
    didTrackInitialRef.current = true
    trackFaq('faq:item:open', {
      slug: 'what-is-xp-lab',
      sectionId: slugToSectionId('what-is-xp-lab'),
      trigger: 'initial',
    })
  }, [])

  useEffect(() => {
    function applyHashState() {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, '').trim())
      if (!hash) return

      if (allFaqSlugs.has(hash)) {
        if (!openItemsRef.current.includes(hash)) {
          trackFaq('faq:item:open', {
            slug: hash,
            sectionId: slugToSectionId(hash),
            trigger: 'hash',
          })
          setOpenItems((prev) => (prev.includes(hash) ? prev : [...prev, hash]))
        }
      }

      window.requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ block: 'start' })
      })
    }

    applyHashState()
    window.addEventListener('hashchange', applyHashState)
    return () => window.removeEventListener('hashchange', applyHashState)
  }, [])

  function getSectionOpenValues(sectionItems: string[]) {
    return sectionItems.filter((v) => openItems.includes(v))
  }

  function handleSectionAccordionChange(
    sectionId: string,
    sectionItems: string[],
    next: string[]
  ) {
    const sectionSet = new Set(sectionItems)
    const prevInSection = new Set(openItems.filter((v) => sectionSet.has(v)))
    const nextInSection = new Set(next)
    for (const slug of nextInSection) {
      if (!prevInSection.has(slug)) {
        trackFaq('faq:item:open', { slug, sectionId, trigger: 'user' })
      }
    }
    for (const slug of prevInSection) {
      if (!nextInSection.has(slug)) {
        trackFaq('faq:item:close', { slug, sectionId })
      }
    }
    setOpenItems((prev) => [...prev.filter((v) => !sectionSet.has(v)), ...next])
  }

  return (
    <>
      <StructuredDataScript id="faq-page-structured-data" data={faqJsonLd} />
      <div className="flex flex-1 w-full justify-center">
        <div className="flex-1 min-w-0 max-w-6xl space-y-10 md:space-y-12 mt-4 md:mt-8 mb-16">
          <section className="px-6 py-8 md:px-10 md:py-12" aria-labelledby="faq-heading">
            <div className="max-w-3xl space-y-6">
              <h1
                id="faq-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground"
              >
                {t('faq.title')}
                <span className="text-accent">.</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {t('faq.subtitle')}
              </p>
            </div>
          </section>

          <section className="px-0" aria-label={t('faq.title')}>
            <div className="rounded-xl border border-border bg-card/50 p-5 md:p-8 space-y-10">
              <div id="getting-started" className="scroll-mt-24 grid gap-5 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.5fr)]">
                <div className="space-y-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">01</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                    {t('faq.sectionProductBasicsTitle')}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('faq.sectionProductBasicsDescription')}
                  </p>
                </div>
                <div className="space-y-4">
                  <SectionExpandControls
                    sectionId={sections[0].id}
                    sectionItems={sections[0].items}
                    openItems={openItems}
                    setOpenItems={setOpenItems}
                  />
                  <Accordion
                    type="multiple"
                    className="w-full"
                    value={getSectionOpenValues(sections[0].items)}
                    onValueChange={(next) =>
                      handleSectionAccordionChange(sections[0].id, sections[0].items, next)
                    }
                  >
                    <AccordionItem value="what-is-xp-lab" id="what-is-xp-lab" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.whatIsXpLab')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p><strong>XP Lab</strong> {t('faq.whatIsXpLabP1Suffix')}</p>
                          <p>{t('faq.whatIsXpLabP2')}</p>
                          <NextStep labelKey="faq.nextStepAbout" href="/about" slug="what-is-xp-lab" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="how-is-xp-lab-different-from-a-sql-course" id="how-is-xp-lab-different-from-a-sql-course" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.howIsXpLabDifferentFromSqlCourse')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.howIsXpLabDifferentFromSqlCourseP1')}</p>
                          <p>{t('faq.howIsXpLabDifferentFromSqlCourseP2')}</p>
                          <NextStep labelKey="faq.nextStepAbout" href="/about" slug="how-is-xp-lab-different-from-a-sql-course" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="who-is-xp-lab-for" id="who-is-xp-lab-for" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.whoIsXpLabFor')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.whoIsXpLabForIntro')}</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.whoIsXpLabForItem1')}</li>
                            <li>{t('faq.whoIsXpLabForItem2')}</li>
                            <li>{t('faq.whoIsXpLabForItem3')}</li>
                            <li>{t('faq.whoIsXpLabForItem4')}</li>
                            <li>{t('faq.whoIsXpLabForItem5')}</li>
                          </ul>
                          <p>{t('faq.whoIsXpLabForOutro')}</p>
                          <NextStep labelKey="faq.nextStepAbout" href="/about" slug="who-is-xp-lab-for" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="what-do-i-get" id="what-do-i-get" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.whatDoIGet')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.whatDoIGetIntro')}</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.whatDoIGetItem1')}</li>
                            <li>{t('faq.whatDoIGetItem2')}</li>
                            <li>{t('faq.whatDoIGetItem3')}</li>
                            <li>{t('faq.whatDoIGetItem4')}</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="is-xp-lab-free" id="is-xp-lab-free" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.isXpLabFree')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.isXpLabFreeP1')}</p>
                          <p>{t('faq.isXpLabFreeP2')}</p>
                          <p>{t('faq.isXpLabFreeP3')}</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="what-skills-should-i-already-have" id="what-skills-should-i-already-have" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.whatSkills')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.whatSkillsIntro')}</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.whatSkillsItem1')}</li>
                            <li>{t('faq.whatSkillsItem2')}</li>
                          </ul>
                          <p>{t('faq.whatSkillsOutroPrefix')} <strong>{t('faq.whatSkillsOutroBold')}</strong>.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="do-i-need-to-install-anything-before-i-start" id="do-i-need-to-install-anything-before-i-start" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.doINeedToInstallAnything')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.doINeedToInstallAnythingP1')}</p>
                          <p>{t('faq.doINeedToInstallAnythingP2')}</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>

              <div aria-hidden className="h-px bg-border/50" />

              <div id="practice-and-feedback" className="scroll-mt-24 grid gap-5 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.5fr)]">
                <div className="space-y-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">02</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                    {t('faq.sectionLearningWorkflowTitle')}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('faq.sectionLearningWorkflowDescription')}
                  </p>
                </div>
                <div className="space-y-4">
                  <SectionExpandControls
                    sectionId={sections[1].id}
                    sectionItems={sections[1].items}
                    openItems={openItems}
                    setOpenItems={setOpenItems}
                  />
                  <Accordion
                    type="multiple"
                    className="w-full"
                    value={getSectionOpenValues(sections[1].items)}
                    onValueChange={(next) =>
                      handleSectionAccordionChange(sections[1].id, sections[1].items, next)
                    }
                  >
                    <AccordionItem value="what-kind-of-tasks-will-i-solve" id="what-kind-of-tasks-will-i-solve" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.whatKindOfTasks')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.whatKindOfTasksIntro')}</p>
                          <p>{t('faq.whatKindOfTasksExamples')}</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.whatKindOfTasksItem1')}</li>
                            <li>{t('faq.whatKindOfTasksItem2')}</li>
                            <li>{t('faq.whatKindOfTasksItem3')}</li>
                            <li>{t('faq.whatKindOfTasksItem4')}</li>
                          </ul>
                          <p>{t('faq.whatKindOfTasksOutroPrefix')} <strong>{t('faq.whatKindOfTasksOutroBold')}</strong>.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="how-long-does-an-assignment-usually-take" id="how-long-does-an-assignment-usually-take" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.howLongDoesAssignmentTake')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.howLongDoesAssignmentTakeP1')}</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.howLongDoesAssignmentTakeItem1')}</li>
                            <li>{t('faq.howLongDoesAssignmentTakeItem2')}</li>
                            <li>{t('faq.howLongDoesAssignmentTakeItem3')}</li>
                          </ul>
                          <p>{t('faq.howLongDoesAssignmentTakeP2')}</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="do-i-get-feedback" id="do-i-get-feedback" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.doIGetFeedback')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.doIGetFeedbackIntro')}</p>
                          <p>{t('faq.doIGetFeedbackP2')}</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.doIGetFeedbackItem1')}</li>
                            <li>{t('faq.doIGetFeedbackItem2')}</li>
                            <li>{t('faq.doIGetFeedbackItem3')}</li>
                          </ul>
                          <p>{t('faq.doIGetFeedbackOutro')}</p>
                          <NextStep labelKey="faq.nextStepFeedbackGuide" href="/xp-lab-feedback" slug="do-i-get-feedback" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="how-does-feedback-work-in-practice" id="how-does-feedback-work-in-practice" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.howDoesFeedbackWorkInPractice')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.howDoesFeedbackWorkInPracticeP1')}</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.howDoesFeedbackWorkInPracticeItem1')}</li>
                            <li>{t('faq.howDoesFeedbackWorkInPracticeItem2')}</li>
                            <li>{t('faq.howDoesFeedbackWorkInPracticeItem3')}</li>
                          </ul>
                          <p>{t('faq.howDoesFeedbackWorkInPracticeP2')}</p>
                          <NextStep labelKey="faq.nextStepFeedbackGuide" href="/xp-lab-feedback" slug="how-does-feedback-work-in-practice" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="can-i-retry-a-challenge-after-i-submit" id="can-i-retry-a-challenge-after-i-submit" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.canIRetryChallengeAfterSubmit')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.canIRetryChallengeAfterSubmitP1')}</p>
                          <p>{t('faq.canIRetryChallengeAfterSubmitP2')}</p>
                          <NextStep labelKey="faq.nextStepFeedbackGuide" href="/xp-lab-feedback" slug="can-i-retry-a-challenge-after-i-submit" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="can-i-use-llms-chatgpt-gemini-claude" id="can-i-use-llms-chatgpt-gemini-claude" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.canIChatGPT')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.canIChatGPTP1')}</p>
                          <p>{t('faq.canIChatGPTP2')}</p>
                          <p>{t('faq.canIChatGPTP3')}</p>
                          <p>{t('faq.canIChatGPTP4')}</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.canIChatGPTItem1')}</li>
                            <li>{t('faq.canIChatGPTItem2')}</li>
                            <li>{t('faq.canIChatGPTItem3')}</li>
                            <li>{t('faq.canIChatGPTItem4')}</li>
                          </ul>
                          <p>{t('faq.canIChatGPTP5')}</p>
                          <NextStep
                            labelKey="faq.nextStepAiJudgmentGuide"
                            href="/using-ai-without-outsourcing-judgment"
                            slug="can-i-use-llms-chatgpt-gemini-claude"
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>

              <div aria-hidden className="h-px bg-border/50" />

              <div id="career-growth" className="scroll-mt-24 grid gap-5 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.5fr)]">
                <div className="space-y-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">03</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                    {t('faq.sectionCareerOutcomesTitle')}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('faq.sectionCareerOutcomesDescription')}
                  </p>
                </div>
                <div className="space-y-4">
                  <SectionExpandControls
                    sectionId={sections[2].id}
                    sectionItems={sections[2].items}
                    openItems={openItems}
                    setOpenItems={setOpenItems}
                  />
                  <Accordion
                    type="multiple"
                    className="w-full"
                    value={getSectionOpenValues(sections[2].items)}
                    onValueChange={(next) =>
                      handleSectionAccordionChange(sections[2].id, sections[2].items, next)
                    }
                  >
                    <AccordionItem value="will-this-help-me-get-a-job" id="will-this-help-me-get-a-job" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.willThisHelpJob')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.willThisHelpJobIntroPrefix')} <strong>{t('faq.willThisHelpJobIntroBold')}</strong>:</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.willThisHelpJobItem1')}</li>
                            <li>{t('faq.willThisHelpJobItem2')}</li>
                            <li>{t('faq.willThisHelpJobItem3')}</li>
                            <li>{t('faq.willThisHelpJobItem4')}</li>
                          </ul>
                          <p>{t('faq.willThisHelpJobOutroPrefix')} <strong>{t('faq.willThisHelpJobOutroBold')}</strong> {t('faq.willThisHelpJobOutroSuffix')}</p>
                          <NextStep labelKey="faq.nextStepAbout" href="/about" slug="will-this-help-me-get-a-job" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="how-does-xp-lab-help-with-take-home-assignments" id="how-does-xp-lab-help-with-take-home-assignments" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.howDoesXpLabHelpTakeHomes')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.howDoesXpLabHelpTakeHomesP1')}</p>
                          <p>{t('faq.howDoesXpLabHelpTakeHomesP2')}</p>
                          <p>{t('faq.howDoesXpLabHelpTakeHomesP3')}</p>
                          <NextStep labelKey="faq.nextStepTakeHomeGuide" href="/take-home-assignments" slug="how-does-xp-lab-help-with-take-home-assignments" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="i-need-help-with-interviews-can-you-help-me" id="i-need-help-with-interviews-can-you-help-me" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.mockInterviewFromCompletedTask')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.mockInterviewFromCompletedTaskP1')}</p>
                          <p>{t('faq.mockInterviewFromCompletedTaskP2')}</p>
                          <p>
                            {t('faq.mockInterviewFromCompletedTaskP3Prefix')}{' '}
                            <a
                              href="mailto:tal@xp-lab.com"
                              onClick={() =>
                                trackFaq('faq:contact:email_click', {
                                  slug: 'i-need-help-with-interviews-can-you-help-me',
                                })
                              }
                              className="text-accent hover:text-foreground transition-colors"
                            >
                              tal@xp-lab.com
                            </a>
                            .
                          </p>
                          <NextStep labelKey="faq.nextStepContact" href="mailto:tal@xp-lab.com" external slug="i-need-help-with-interviews-can-you-help-me" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="what-is-anonymous-opportunity-matching" id="what-is-anonymous-opportunity-matching" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.anonymousOpportunityMatching')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.anonymousOpportunityMatchingP1')}</p>
                          <NextStep labelKey="faq.nextStepAbout" href="/about" slug="what-is-anonymous-opportunity-matching" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>

              <div aria-hidden className="h-px bg-border/50" />

              <div id="privacy-bugs-and-access" className="scroll-mt-24 grid gap-5 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.5fr)]">
                <div className="space-y-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">04</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                    {t('faq.sectionTrustOperationsTitle')}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('faq.sectionTrustOperationsDescription')}
                  </p>
                </div>
                <div className="space-y-4">
                  <SectionExpandControls
                    sectionId={sections[3].id}
                    sectionItems={sections[3].items}
                    openItems={openItems}
                    setOpenItems={setOpenItems}
                  />
                  <Accordion
                    type="multiple"
                    className="w-full"
                    value={getSectionOpenValues(sections[3].items)}
                    onValueChange={(next) =>
                      handleSectionAccordionChange(sections[3].id, sections[3].items, next)
                    }
                  >
                    <AccordionItem value="is-my-work-private" id="is-my-work-private" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.isMyWorkPrivate')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>
                            {t('faq.isMyWorkPrivateAnswerPrefix')}{' '}
                            <Link
                              to="/privacy-policy"
                              onClick={() =>
                                trackFaq('faq:privacy_policy:click', {
                                  slug: 'is-my-work-private',
                                })
                              }
                              className="text-accent hover:text-foreground transition-colors underline underline-offset-4"
                            >
                              {t('common.privacyPolicy')}
                            </Link>
                            .
                          </p>
                          <NextStep labelKey="faq.nextStepPrivacy" href="/privacy-policy" slug="is-my-work-private" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="what-happens-during-the-private-beta" id="what-happens-during-the-private-beta" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.whatHappensDuringPrivateBeta')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.whatHappensDuringPrivateBetaP1')}</p>
                          <ul className="list-disc space-y-2 ms-6">
                            <li>{t('faq.whatHappensDuringPrivateBetaItem1')}</li>
                            <li>{t('faq.whatHappensDuringPrivateBetaItem2')}</li>
                            <li>{t('faq.whatHappensDuringPrivateBetaItem3')}</li>
                          </ul>
                          <NextStep labelKey="faq.nextStepContact" href="mailto:tal@xp-lab.com" external slug="what-happens-during-the-private-beta" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="when-is-it-launching" id="when-is-it-launching" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.whenIsLaunching')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>
                            {t('faq.whenIsLaunchingPrefix')}{' '}
                            <button
                              type="button"
                              onClick={() => {
                                trackFaq('faq:waitlist:click', {
                                  slug: 'when-is-it-launching',
                                })
                                trackMarketing('waiting_list:open', {
                                  source: 'faq_when_is_it_launching',
                                })
                                setIsWaitlistOpen(true)
                              }}
                              className="text-accent hover:text-foreground transition-colors underline underline-offset-4"
                            >
                              {t('faq.waitlist')}
                            </button>{' '}
                            {t('faq.whenIsLaunchingOrContact')}{' '}
                            <a
                              href="mailto:tal@xp-lab.com"
                              onClick={() =>
                                trackFaq('faq:contact:email_click', {
                                  slug: 'when-is-it-launching',
                                })
                              }
                              className="text-accent hover:text-foreground transition-colors"
                            >
                              tal@xp-lab.com
                            </a>
                            .
                          </p>
                          <NextStep labelKey="faq.nextStepContact" href="mailto:tal@xp-lab.com" external slug="when-is-it-launching" />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="i-found-a-bug" id="i-found-a-bug" className="scroll-mt-24">
                      <AccordionTrigger>{t('faq.foundABug')}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <p>{t('faq.foundABugAnswerPrefix')}</p>
                          <FaqBugReportLink />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>
            </div>
          </section>

          <Dialog open={isWaitlistOpen} onOpenChange={setIsWaitlistOpen}>
            <DialogContent className="sm:max-w-lg">
              <WaitingList source="faq_when_is_it_launching" />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  )
}
